/**
 * DataFreshnessWidget - Displays data freshness status for all engines
 * 
 * Migrated from seo-engine-webtools-customer-module
 */

import { Box, Card, CardContent, Typography, LinearProgress, Chip } from '@mui/material';
import { DataFreshnessResponse, EngineFreshness } from '@/types/customer';
import { ENGINE_COLORS, ENGINE_LABELS, STATUS_COLORS } from '@/services/customer/constants';
import { formatRelativeTime } from '@/services/customer/formatters';

interface Props {
  data: DataFreshnessResponse;
}

function DataFreshnessWidget({ data }: Props) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Data Freshness</Typography>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h4" color="primary" fontWeight="bold">
              {data.overallHealthScore}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Health Score
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {data.engines.map((engine) => (
            <EngineRow key={engine.engineType} engine={engine} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

interface EngineRowProps {
  engine: EngineFreshness;
}

function EngineRow({ engine }: EngineRowProps) {
  const engineColor = ENGINE_COLORS[engine.engineType] || '#757575';
  const statusColor = STATUS_COLORS[engine.healthStatus] || STATUS_COLORS.MISSING;

  const freshCount = engine.dataTypes.filter(dt => dt.freshnessStatus === 'FRESH').length;
  const totalCount = engine.dataTypes.length;
  const freshnessPercent = totalCount > 0 ? (freshCount / totalCount) * 100 : 0;

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: engineColor,
            }}
          />
          <Typography variant="subtitle2">
            {ENGINE_LABELS[engine.engineType]}
          </Typography>
        </Box>
        <Chip
          label={engine.healthStatus}
          size="small"
          sx={{
            bgcolor: statusColor.bg,
            color: statusColor.color,
            fontWeight: 'medium',
          }}
        />
      </Box>

      <LinearProgress
        variant="determinate"
        value={freshnessPercent}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': {
            bgcolor: engineColor,
          },
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {freshCount}/{totalCount} data types fresh
        </Typography>
        {engine.lastSuccessfulSync && (
          <Typography variant="caption" color="text.secondary">
            Last sync: {formatRelativeTime(engine.lastSuccessfulSync)}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default DataFreshnessWidget;
